import { useMemo, useRef, useState } from "react";

import { SearchRounded } from "@mui/icons-material";
import { Box, ButtonBase, Fade } from "@mui/material";
import { keyframes } from "@mui/system";
import PropTypes from "prop-types";

import Txt from "../../../../../components/typography/Txt";
import { slate, teal } from "../../../../../theme/colors";
import { useStageRecordFiles } from "../../../shared/RecordDocumentsDropzone";
import { MENU_ITEMS, useAddToLoanItems } from "./addToLoanCatalog";

// The card eases in from the rail (right edge) with a slight lift; its rows
// follow a beat behind, staggered, so the list reads as "unfolding" rather
// than blinking on.
const cardIn = keyframes`
  from { opacity: 0; transform: translateX(8px) scale(.97); }
  to   { opacity: 1; transform: none; }
`;
const rowIn = keyframes`
  from { opacity: 0; transform: translateX(6px); }
  to   { opacity: 1; transform: none; }
`;
const EASE = "cubic-bezier(.2,.8,.2,1)";

/**
 * Props for the MUI `<Tooltip>` that hosts the menu — kept beside the menu so
 * the card's look and its entrance animation live in one file.
 */
export const quickAddTooltipProps = {
  // The theme defaults every Tooltip to `disableInteractive` + `arrow`; the
  // menu has to stay open while the pointer travels into it, and has no arrow.
  disableInteractive: false,
  arrow: false,
  placement: "left-start",
  enterDelay: 120,
  enterNextDelay: 60,
  leaveDelay: 140,
  TransitionComponent: Fade,
  TransitionProps: { timeout: { enter: 0, exit: 120 } },
  componentsProps: {
    popper: {
      modifiers: [{ name: "offset", options: { offset: [-6, 6] } }],
    },
    tooltip: {
      sx: {
        p: 0,
        m: "0 !important",
        maxWidth: "none",
        bgcolor: "#fff",
        color: slate[900],
        border: `1px solid ${slate[200]}`,
        borderRadius: "12px",
        boxShadow:
          "0 1px 2px rgba(15,23,42,.06), 0 12px 32px -4px rgba(15,23,42,.18)",
        transformOrigin: "right top",
        animation: `${cardIn} .2s ${EASE} both`,
      },
    },
  },
};

const row = (i) => ({
  width: "100%",
  justifyContent: "flex-start",
  gap: "12px",
  minHeight: 40,
  px: "8px",
  py: "6px",
  borderRadius: "8px",
  textAlign: "left",
  color: slate[900],
  animation: `${rowIn} .22s ${EASE} both`,
  animationDelay: `${40 + i * 22}ms`,
  transition: "background-color .12s, color .12s",
  "& .qa-ic": {
    width: 30,
    height: 30,
    flex: "none",
    borderRadius: "8px",
    display: "grid",
    placeItems: "center",
    background: slate[100],
    color: slate[500],
    transition: "background-color .12s, color .12s",
    "& .MuiSvgIcon-root": { fontSize: 17 },
  },
  "&:hover, &:focus-visible, &.is-over": {
    background: slate[50],
    "& .qa-ic": { background: teal[100], color: teal[700] },
  },
});

const labelSx = { fontSize: 13.5, fontWeight: 500, lineHeight: "18px" };

/**
 * The Quick Add rail button's hover menu: a fixed set of everyday adds, and
 * a way into the full Add to loan panel. Same catalog, gating and pick
 * behaviour as the panel. The Upload row is also a drop target — files
 * dropped on it are staged into the upload modal's review queue.
 */
const QuickAddHoverMenu = ({ onDone, onOpenAll }) => {
  const { available, select } = useAddToLoanItems();
  const stageFiles = useStageRecordFiles(null);
  const [dropOver, setDropOver] = useState(false);
  const dragDepth = useRef(0);

  const items = useMemo(
    () =>
      MENU_ITEMS.map((m) => {
        const item = available.find((a) => a.key === m.key);
        return item ? { ...m, item } : null;
      }).filter(Boolean),
    [available],
  );

  const dropProps = {
    onDragEnter: (e) => {
      e.preventDefault();
      dragDepth.current += 1;
      setDropOver(true);
    },
    onDragOver: (e) => e.preventDefault(),
    onDragLeave: () => {
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (!dragDepth.current) setDropOver(false);
    },
    onDrop: (e) => {
      e.preventDefault();
      dragDepth.current = 0;
      setDropOver(false);
      if (!e.dataTransfer?.files?.length) return;
      onDone?.();
      stageFiles(e.dataTransfer.files);
    },
  };

  return (
    <Box sx={{ width: 256, p: "6px" }}>
      <Txt
        component="div"
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: slate[500],
          px: "8px",
          pt: "6px",
          pb: "6px",
        }}
      >
        Quick add
      </Txt>

      {items.map(({ key, action, hint, item }, i) => {
        const Icon = item.icon;
        const isUpload = !!hint;
        return (
          <ButtonBase
            key={key}
            disableRipple
            className={isUpload && dropOver ? "is-over" : undefined}
            sx={{
              ...row(i),
              ...(isUpload && dropOver
                ? { outline: `1.5px dashed ${teal[400]}`, outlineOffset: -1 }
                : {}),
            }}
            onClick={() => {
              onDone?.();
              select(item);
            }}
            {...(isUpload ? dropProps : {})}
          >
            <span className="qa-ic">
              <Icon />
            </span>
            <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
              <Txt sx={labelSx}>{action}</Txt>
              {hint && (
                <Txt sx={{ fontSize: 12, color: slate[500], lineHeight: "16px" }}>
                  {dropOver ? "Release to stage for upload" : hint}
                </Txt>
              )}
            </Box>
          </ButtonBase>
        );
      })}

      <Box sx={{ height: "1px", bgcolor: slate[200], mx: "8px", my: "6px" }} />

      <ButtonBase
        disableRipple
        sx={{
          ...row(items.length),
          color: teal[700],
          "&:hover, &:focus-visible": {
            background: teal[50],
            "& .qa-ic": { background: teal[100], color: teal[700] },
          },
        }}
        onClick={() => {
          onDone?.();
          onOpenAll?.();
        }}
      >
        <span className="qa-ic">
          <SearchRounded />
        </span>
        <Txt sx={{ ...labelSx, flex: 1 }}>Browse all items</Txt>
        <Txt
          sx={{
            fontSize: 12,
            color: slate[400],
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {available.length}
        </Txt>
      </ButtonBase>
    </Box>
  );
};

QuickAddHoverMenu.propTypes = {
  onDone: PropTypes.func,
  onOpenAll: PropTypes.func,
};

export default QuickAddHoverMenu;
